import { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { UpsellProduct } from './UpsellProduct.jsx';
import { ImageWrapper } from './ImageWrapper.jsx';
import { ChevronLeft, Minus, Plus } from 'lucide-react';
import { getProductInfo } from '../utils/productHelpers.js';
import { getSuggestionUnitPrice, getUpsellShownIds } from '../utils/suggestions.js';
import { sortModifierGroups, sortModifierOptions, buildExtraKey, normalizeSelectedExtras } from '../utils/modifierHelpers.js';
import { Drawer, DrawerContent, DrawerClose } from './ui/drawer';
import { Checkbox } from './ui/checkbox';
import { RadioGroup } from './ui/radio-group';
import { getMaxPerItem } from '../hooks/useItems.js';
import Currency from '../utils/Currency.js';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import locationIcon from '../images/location.svg';

export function ProductDetailModal({ currency, locale, storeId, product, validProducts, modifiers, sellers, sellerColors, open, onClose, onUpsellClick, onAddToCart, getProductById, filteredStoreIds, logActivity, drawerContainer, initialQuantity = 1, initialSelectedExtras = null, editMode = false }) {
  const [quantity, setQuantity] = useState(1);
  const [selectedExtras, setSelectedExtras] = useState({});
  const [shakingGroup, setShakingGroup] = useState(null);
  const seededProductRef = useRef(null);

  // Grupos y opciones siempre en el mismo orden: el `groupIndex` de las llaves de
  // `selectedExtras` depende de esta posición, así que debe ser estable.
  const sortedGroups = useMemo(() => {
    return sortModifierGroups(modifiers).map(extraGroup => ({
      ...extraGroup,
      options: sortModifierOptions(extraGroup.options)
    }));
  }, [modifiers]);

  // Trigger shake animation for a group
  const triggerShake = (groupIndex) => {
    setShakingGroup(groupIndex);
    setTimeout(() => setShakingGroup(null), 400); // 400ms duration for the shake
  };

  // Reset state cuando se abre un nuevo producto
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setQuantity(1);
      setSelectedExtras({});
      onClose();
    }
  };

  // Pre-seleccion automática para grupos cerrados (obligatorios donde max == min y la capacidad de las opciones == max)
  const getAutoSelectedExtras = (groups) => {
    const initialExtras = {};

    (groups || []).forEach((extraGroup, groupIndex) => {
      if (extraGroup.min !== undefined && extraGroup.max !== undefined && extraGroup.min === extraGroup.max) {

        // Calcular la capacidad total máxima que ofrecen las opciones de este grupo
        const totalOptionsCap = extraGroup.options.reduce((sum, opt) => sum + (opt.max_per_item || 1), 0);

        // Si la capacidad total de las opciones disponibles es exactamente el máximo requerido, auto-completar
        if (totalOptionsCap === extraGroup.max) {
          extraGroup.options.forEach(opt => {
            if (extraGroup.max <= 1) {
               initialExtras[buildExtraKey(extraGroup.id, opt.id, groupIndex)] = 1;
            } else {
               initialExtras[buildExtraKey(extraGroup.id, opt.id, groupIndex)] = opt.max_per_item || 1;
            }
          });
        }
      }
    });

    return initialExtras;
  };

  // Inicializa cantidad y modificadores al abrir el modal (o al cambiar de producto vía upsell).
  // Si viene `initialSelectedExtras` (edición desde el carro) se respeta esa selección,
  // normalizada al formato de llaves actual; si no, se aplica la pre-selección automática.
  useEffect(() => {
    if (!open) {
      seededProductRef.current = null;
      return;
    }
    if (!product) return;
    if (seededProductRef.current === product.id) return;

    const isFirstSeed = seededProductRef.current === null;
    seededProductRef.current = product.id;

    // La cantidad sólo se inicializa al abrir; si se cambia de producto vía upsell se conserva
    if (isFirstSeed) setQuantity(initialQuantity > 0 ? initialQuantity : 1);

    setSelectedExtras(
      isFirstSeed && initialSelectedExtras
        ? normalizeSelectedExtras(sortedGroups, initialSelectedExtras)
        : getAutoSelectedExtras(sortedGroups)
    );
  }, [open, product, sortedGroups, initialQuantity, initialSelectedExtras]);

  // Exposición del upsell (SOK-06): una vez por apertura, con la lista que vio el cliente al
  // abrir la ficha. Cambiar de producto vía upsell no cuenta como otra exposición.
  const upsellShownRef = useRef(false);
  useEffect(() => {
    if (!open) {
      upsellShownRef.current = false;
      return;
    }
    if (!product || editMode || upsellShownRef.current) return;
    upsellShownRef.current = true;
    const validUpsell = (product.upsell || []).filter(item => validProducts.some(p => p.id === item.product_id));
    const upsellProductIds = getUpsellShownIds(product.id, validUpsell);
    if (upsellProductIds.length === 0) return;
    logActivity('product-details', 'upsell-list', 'shown', false, {product_id: product.id, product_name: product.label || product.name || null, upsell_product_ids: upsellProductIds});
  }, [open, product, editMode, validProducts, logActivity]);

  // Calcular precio total
  const totalPrice = useMemo(() => {
    if (!product) return 0;

    const { commonPrice } = getProductInfo(product, filteredStoreIds);

    let total = commonPrice * quantity;

    // Agregar precio de extras (si algún extra tiene precio definido)
    sortedGroups.forEach((extraGroup, groupIndex) => {
      extraGroup.options.forEach(extra => {
        if (extra.price) {
          const value = selectedExtras[buildExtraKey(extraGroup.id, extra.id, groupIndex)];
          if (typeof value === 'number' && value > 0) {
            total += extra.price * value * quantity;
          } else if (value === true) {
            total += extra.price * quantity;
          }
        }
      });
    });

    return total;
  }, [product, sortedGroups, quantity, selectedExtras, filteredStoreIds]);

  const countSelectedInGroupObj = (extraGroup, items, groupIndex, extrasObj) => {
    return items.reduce((count, extra) => {
      const value = extrasObj[buildExtraKey(extraGroup.id, extra.id, groupIndex)];
      if (typeof value === 'number' && value > 0) {
        return count + value;
      } else if (value === true) {
        return count + 1;
      }
      return count;
    }, 0);
  };

  // Contar extras seleccionados en un grupo
  const countSelectedInGroup = (extraGroup, items, groupIndex) => {
    return countSelectedInGroupObj(extraGroup, items, groupIndex, selectedExtras);
  };

  const getValidUpsellProducts = () => {
    const filtered = product.upsell.filter(upsellItem => {
      const match = validProducts.find(p => p.id === upsellItem.product_id);
      return match !== undefined;
    });
    const sorted = filtered.sort((p1, p2) => {
      const p1Priority = p1.priority !== null && p1.priority !== undefined ? p1.priority : Number.MAX_SAFE_INTEGER;
      const p2Priority = p2.priority !== null && p2.priority !== undefined ? p2.priority : Number.MAX_SAFE_INTEGER;
      return p1Priority - p2Priority;
    });
    return sorted;
  };

  // Manejar cambio de checkbox
  const handleCheckboxChange = (extraGroupId, groupIndex, extraId, checked) => {
    setSelectedExtras(prev => {
      if (checked) {
        const extraGroup = sortedGroups[groupIndex];
        const selectedCount = countSelectedInGroupObj(extraGroup, extraGroup.options, groupIndex, prev);
        
        if (extraGroup.max !== undefined && selectedCount + 1 > extraGroup.max) {
           return prev;
        }
      }

      return {
        ...prev,
        [buildExtraKey(extraGroupId, extraId, groupIndex)]: (checked ? 1 : 0)
      };
    });
  };

  const handleRadioGroupChange = (extraGroupId, groupIndex, options, value) => {
    // Deseleccionar todas las opciones del grupo
    const newExtras = { ...selectedExtras };
    options.forEach(opt => {
      if (newExtras[buildExtraKey(extraGroupId, opt.id, groupIndex)] > 0) {
        delete newExtras[buildExtraKey(extraGroupId, opt.id, groupIndex)];
      }
    });

    // Deseleccionar "Ninguno"
    if (newExtras[buildExtraKey(extraGroupId, 0, groupIndex)] > 0) {
      delete newExtras[buildExtraKey(extraGroupId, 0, groupIndex)];
    }

    // Seleccionar la nueva opción
    if (value) {
      const optionKey = buildExtraKey(extraGroupId, value, groupIndex);
      logActivity('product-details', 'modifier-option', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, quantity, selected_extra: {[optionKey]: 1}});
      newExtras[optionKey] = 1;
    }
    setSelectedExtras(newExtras);
  }

  // Manejar cambio de cantidad de extra
  const handleExtraQuantityChange = (extraGroupId, groupIndex, extraId, delta, maxQuantity = undefined) => {
    setSelectedExtras(prev => {
      if (delta > 0) {
        const extraGroup = sortedGroups[groupIndex];
        const selectedCount = countSelectedInGroupObj(extraGroup, extraGroup.options, groupIndex, prev);
        
        if (extraGroup.max !== undefined && selectedCount + delta > extraGroup.max) {
           return prev; // Limita si excede el máximo general de la categoría
        }
      }

      const key = buildExtraKey(extraGroupId, extraId, groupIndex);
      const current = (typeof prev[key] === 'number' ? prev[key] : 0);
      const newValue = Math.max(0, current + delta);
      const finalValue = maxQuantity ? Math.min(newValue, maxQuantity) : newValue;

      return {
        ...prev,
        [key]: finalValue
      };
    });
  };

  // Validar que se cumplan las condiciones mínimas y máximas de extras
  const isValid = useMemo(() => {
    if (sortedGroups.length === 0) return true;

    for (let groupIndex = 0; groupIndex < sortedGroups.length; groupIndex++) {
      const extraGroup = sortedGroups[groupIndex];
      const selectedCount = countSelectedInGroup(extraGroup, extraGroup.options, groupIndex);
      
      if (extraGroup.min !== undefined && selectedCount < extraGroup.min) return false;
      if (extraGroup.max !== undefined && selectedCount > extraGroup.max) return false;
    }

    return true;
  }, [sortedGroups, countSelectedInGroup]);

  const handleAddToCart = () => {
    if (!isValid) {
      for (let groupIndex = 0; groupIndex < sortedGroups.length; groupIndex++) {
        const extraGroup = sortedGroups[groupIndex];
        const selectedCount = countSelectedInGroup(extraGroup, extraGroup.options, groupIndex);
        if (extraGroup.min !== undefined && selectedCount < extraGroup.min) {
          triggerShake(groupIndex);
          const element = document.getElementById(`extra-group-${groupIndex}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          break;
        }
      }
      return;
    }

    if (product) {
      logActivity('product-details', editMode ? 'save-changes-button' : 'add-to-cart-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, quantity, selected_extras: selectedExtras});
      onAddToCart(product, quantity, selectedExtras);
      handleOpenChange(false);
    }
  };

  const handleQuantityIncrease = () => {
    const maxPerItem = getMaxPerItem(product, storeId, filteredStoreIds);
    if (quantity >= maxPerItem) return;
    const newQuantity = quantity + 1;
    logActivity('product-details', 'increase-quantity-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, old_quantity: quantity, new_quantity: newQuantity});
    setQuantity(newQuantity);
  };

  const handleQuantityDecrease = () => {
    const newQuantity = Math.max(1, quantity - 1);
    logActivity('product-details', 'decrease-quantity-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, old_quantity: quantity, new_quantity: newQuantity});
    setQuantity(newQuantity);
  };

  const handleUpsellClick = (upsellProduct) => {
    logActivity('product-details', 'upsell-product', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, upsell_product_id: upsellProduct.id, upsell_product_name: upsellProduct.label || upsellProduct.name || null, unit_price: getSuggestionUnitPrice(upsellProduct, filteredStoreIds)});
    onUpsellClick(upsellProduct.id);
  };

  const handleBack = () => {
    logActivity('product-details', 'back-button', 'back', true, {product_id: product.id, product_name: product.label || product.name || null});
  };

  const getSellerIndex = (sellerName) => {
    let index = sellers.findIndex(s => s === sellerName);
    if (index > sellerColors.length - 1) return index % sellerColors.length;
    return index !== -1 ? index : 0;
  };

  if (!product) return null;

  const { commonPrice, normalPrice, mainImage } = getProductInfo(product, filteredStoreIds);
  const sellerIndex = getSellerIndex(product.seller_name);
  const sellerColor = sellerColors[sellerIndex];

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent className="max-h-[calc(var(--app-h)*0.9)] bg-back border-none rounded-t-l overflow-hidden" container={drawerContainer}>
        <div className="flex flex-col h-full overflow-hidden">
          {/* Contenido scrolleable */}
          <div className="flex-1 overflow-y-auto px-8 pt-8 pb-48">

            {/* Imagen del producto */}
            <div className="flex justify-center mb-10">
              <div className="w-60 h-60 rounded-[60px] overflow-hidden relative" style={{backgroundColor: '#f3f4f6'}}>
                <ImageWrapper
                  image={mainImage}
                  alt={product.label || product.name}
                  display="block"
                  width="100%"
                  height="100%"
                  backgroundColor="transparent"
                  margin="0"
                  padding="0"
                  borderRadius="30px"
                  transition={true}
                  loadDelay={0.1}
                  onLoad={undefined}
                />
                {product.seller_name ? (
                  <div
                    className="secondary-font-bold fs-16 px-10 py-2 w-full absolute bottom-0 left-0 flex items-center gap-2"
                    style={{color: '#FFFFFF', background: sellerColor, textShadow: '#000000 2px 2px 2px'}}
                  >
                    <ImageWrapper
                      image={locationIcon}
                      alt="Location"
                      display="block"
                      width="24px"
                      height="24px"
                      backgroundColor="transparent"
                      margin="0"
                      padding="0"
                      borderRadius="0"
                      transition={true}
                      loadDelay={0.1}
                      onLoad={undefined}
                    />
                    <div className="overflow-hidden text-ellipsis line-clamp-1 pr-[3px]">
                      {product.seller_name}
                    </div>
                  </div>
                  ) : null}
              </div>
            </div>

            {/* Nombre y descripción de producto */}
            <div className="text-center space-y-4">
              <h1 className='text-center text-4xl text-bold text-detail-title'>
                {product.label || product.name}
              </h1>
              <p className="text-center text-2xl text-muted leading-relaxed text-detail-description">
                {product.description}
              </p>
              {/* Precios unitarios*/}
              <div className="flex items-center gap-2 justify-center">
                <span className="text-3xl text-common-price text-center">
                  {(commonPrice) ? new Currency(currency, locale).format(commonPrice) : "No disponible"}
                </span>
                {(commonPrice && normalPrice && commonPrice < normalPrice) ? <span className="text-sm text-muted line-through text-center">
                  {normalPrice ? new Currency(currency, locale).format(normalPrice) : "No disponible"}
                </span> : ""}
              </div>
            </div>

            {/* Selector de cantidad */}
            <div className="mt-5 mb-8">
              <div className="flex justify-center mb-6">
                <div className="w-[141px] h-[45px] rounded-[130px] border border-muted border-solid flex items-center justify-center">
                  <p className="secondary-font-bold text-center text-[#5e5e5e] fs-20 lh-35">
                    Cantidad
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4">
                <motion.button
                  onClick={handleQuantityDecrease}
                  className="w-[94px] h-[94px] rounded-[20px] border-2 flex items-center justify-center hover:border-[#DE2D7A] transition-colors cursor-pointer"
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.1 }}
                  disabled={quantity <= 1}
                  style={{ borderColor: 'var(--color-text-muted)' }}
                >
                  <Minus className="w-10 h-10" style={{strokeWidth: 3}}/>
                </motion.button>

                <div className="w-[125px] h-[93px] bg-back rounded-[20px] flex items-center justify-center">
                  <span className="text-bold fs-45 lh-25 text-detail-title">
                    {quantity.toString().padStart(2, '0')}
                  </span>
                </div>

                <motion.button
                  onClick={handleQuantityIncrease}
                  className="w-[94px] h-[94px] rounded-[20px] border-2 flex items-center justify-center hover:border-[#DE2D7A] transition-colors cursor-pointer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ borderColor: 'var(--color-text-muted)' }}
                >
                  <Plus className="w-10 h-10" style={{strokeWidth: 3}}/>
                </motion.button>
              </div>
            </div>

            {/* Upsell (oculto al editar: cambiar de producto no aplica sobre un item ya en el carro) */}
            {!editMode && product.upsell && product.upsell.length > 0 && (
              <div className="space-y-8 max-w-screen-xl mx-auto my-4">
                {getValidUpsellProducts().map((upsellItemData, upsellIndex) => {
                  const upsellProduct = getProductById(upsellItemData.product_id);
                  const upsellProductInfo = getProductInfo(upsellProduct, filteredStoreIds);
                  const upsellCommonPrice = upsellProductInfo?.commonPrice;
                  const isSelected = upsellProduct.id === product.id;
                  return (
                    <UpsellProduct
                      key={`upsell-${upsellIndex}`}
                      currency={currency}
                      locale={locale}
                      index={upsellIndex}
                      product={upsellProduct}
                      upsell={upsellItemData}
                      isSelected={isSelected}
                      highlight={upsellItemData.highlight}
                      price={upsellCommonPrice}
                      onClick={handleUpsellClick}
                    />
                  );
                })}
              </div>
            )}

            {/* Extras */}
            {sortedGroups.length > 0 && (
              <div className="space-y-8 max-w-screen-xl mx-auto">

                {sortedGroups.map((extraGroup, groupIndex) => {
                  const extraGroupOptions = extraGroup.options;
                  const selectedCount = countSelectedInGroup(extraGroup, extraGroupOptions, groupIndex);

                  return (
                    <div key={`extra-group-${groupIndex}`} className="space-y-4">
                      {/* Título del grupo de extras */}
                      <motion.div
                        id={`extra-group-${groupIndex}`}
                        className="text-center"
                        animate={shakingGroup === groupIndex ? { x: [-20, 20, -20, 20, 0] } : { x: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <p className="main-font-bold fs-25 text-[var(--modifier-title-color)] mb-1">
                          {extraGroup.name}
                        </p>
                        {extraGroup.min == 1 && (
                          <p className="text-[red] text-sm main-font-bold">
                            * Obligatorio 1 extra
                          </p>
                        )}
                        {extraGroup.min > 1 && (
                          <p className="text-[red] text-sm main-font-bold">
                            * Obligatorio {extraGroup.min} extras
                          </p>
                        )}
                        {extraGroup.max && (
                          <p className="text-[var(--modifier-title-color)] text-sm main-font-bold">
                            Seleccionados {selectedCount}/{extraGroup.max}
                          </p>
                        )}
                      </motion.div>

                      {/* Items del grupo */}
                      <div className="space-y-3 secondary-font-regular cursor-pointer">

                        {(extraGroup.max <= 1 && extraGroupOptions.length > 1) ? (
                          <RadioGroup
                            value={
                              selectedExtras[buildExtraKey(extraGroup.id, 0, groupIndex)] > 0
                                ? "0"
                                : extraGroupOptions.find(opt => (selectedExtras[buildExtraKey(extraGroup.id, opt.id, groupIndex)] === true || selectedExtras[buildExtraKey(extraGroup.id, opt.id, groupIndex)] > 0))?.id || ''
                            }
                            onValueChange={(value) => handleRadioGroupChange(extraGroup.id, groupIndex, extraGroupOptions, value)}
                            className="space-y-0"
                          >
                            {
                              [
                                ( extraGroup.min <= 0 && 
                                  <RadioGroupPrimitive.Item
                                  key={buildExtraKey(extraGroup.id, 0, groupIndex)}
                                  value={"0"}
                                  className=""
                                >
                                  <motion.div
                                    className="cursor-pointer flex items-center justify-between h-[114px] px-[54px] bg-mid rounded-[30px]"
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.9 }}
                                  >
                                    <span className="text-bold fs-25 lh-25">
                                      No Gracias
                                    </span>
                                    <div className="relative w-[64px] h-[64px] bg-[var(--color-primary-label)]/10 rounded-full border-2 border-[var(--color-primary)] data-[state=checked]:border-[var(--color-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 drop-shadow-[0_0_2px_var(--color-primary-label)]/10">
                                      <RadioGroupPrimitive.Indicator className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-[40px] h-[40px] rounded-full bg-[var(--color-primary)]" />
                                      </RadioGroupPrimitive.Indicator>
                                    </div>
                                  </motion.div>
                                </RadioGroupPrimitive.Item>
                                ),
                                extraGroupOptions.map(option => {
                                  return (
                                    <RadioGroupPrimitive.Item
                                      key={buildExtraKey(extraGroup.id, option.id, groupIndex)}
                                      value={option.id}
                                      className=""
                                    >
                                      <motion.div
                                        className="cursor-pointer flex items-center justify-between h-[114px] px-[54px] bg-mid rounded-[30px]"
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.9 }}
                                      >
                                        <span className="text-bold fs-25 lh-25">
                                          {option.name}
                                        </span>
                                        <div className="flex items-center justify-end gap-4">
                                          {option.price && option.price > 0 ?
                                            <span className="text-[var(--color-common-price)] fs-25 lh-25 text-right">
                                              +{new Currency(currency, locale).format(option.price)}
                                            </span> : ""
                                          }
                                          <div className="relative w-[64px] h-[64px] bg-[var(--color-primary-label)]/10 rounded-full border-2 border-[var(--color-primary)] data-[state=checked]:border-[var(--color-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 drop-shadow-[0_0_2px_var(--color-primary-label)]/10">
                                            <RadioGroupPrimitive.Indicator className="absolute inset-0 flex items-center justify-center">
                                              <div className="w-[40px] h-[40px] rounded-full bg-[var(--color-primary)]" />
                                            </RadioGroupPrimitive.Indicator>
                                          </div>
                                        </div>
                                      </motion.div>
                                    </RadioGroupPrimitive.Item>
                                  );
                                })
                              ]
                            }
                          </RadioGroup>
                        ) : (
                          (extraGroupOptions.every(opt => opt.max_per_item <= 1) ? (
                            extraGroupOptions.map(option => {
                              const isChecked = selectedExtras[buildExtraKey(extraGroup.id, option.id, groupIndex)] === true || selectedExtras[buildExtraKey(extraGroup.id, option.id, groupIndex)] > 0;

                              return (
                                <motion.div
                                  key={option.id}
                                  className="a11y-modifier-row cursor-pointer flex items-center justify-between h-[114px] px-[54px] bg-mid rounded-[30px]"
                                  onClick={() => handleCheckboxChange(extraGroup.id, groupIndex, option.id, !isChecked)}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <span className="a11y-modifier-info">
                                    <span className="text-bold fs-25 lh-25">
                                      {option.name}
                                    </span>
                                    <span className="a11y-modifier-meta">
                                      {option.price && option.price > 0 ?
                                        <span className="text-[var(--color-common-price)] fs-25 lh-25 text-right">
                                          +{new Currency(currency, locale).format(option.price)}
                                        </span> : ""
                                      }
                                    </span>
                                  </span>
                                  <div className="relative">
                                    {(!isChecked && extraGroup.max !== undefined && selectedCount >= extraGroup.max) && (
                                      <div 
                                        className="absolute inset-0 z-10 cursor-pointer"
                                        onClick={() => {
                                          triggerShake(groupIndex);
                                        }}
                                      />
                                    )}
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(checked) =>
                                        handleCheckboxChange(extraGroup.id, groupIndex, option.id, checked === true)
                                      }
                                      disabled={!isChecked && extraGroup.max !== undefined ? selectedCount >= extraGroup.max : false}
                                      className="text-xl cursor-pointer bg-[var(--color-primary-label)]/10 w-[59px] h-[59px] rounded-[15px] border-[2px] border-[var(--color-primary)] data-[state=checked]:bg-[var(--color-primary)] data-[state=checked]:border-[var(--color-primary)] drop-shadow-[0_0_2px_var(--color-primary-label)]/10"
                                    />
                                  </div>
                                </motion.div>
                              );
                            })
                          ) : (
                            extraGroupOptions.map(option => {
                              const extraQuantity = (typeof selectedExtras[buildExtraKey(extraGroup.id, option.id, groupIndex)] === 'number'
                                ? selectedExtras[buildExtraKey(extraGroup.id, option.id, groupIndex)]
                                : 0);

                              return (
                                <div
                                  key={option.id}
                                  className="a11y-modifier-row cursor-pointer flex items-center justify-between h-[114px] px-[54px] bg-mid rounded-[30px]"
                                >
                                  {/* SOK-09: sin el modo, a11y-modifier-info y -meta son display: contents y la
                                      fila se ve igual que antes. Con "Letra grande" el nombre va en una línea
                                      y el máximo y el precio en la de abajo (index.css). */}
                                  <span className="a11y-modifier-info">
                                    <span className="text-bold fs-25 lh-25">
                                      {option.name} {option.max_per_item && <span className="a11y-modifier-max-inline text-sm">(Max. {option.max_per_item})</span>}
                                    </span>
                                    <span className="a11y-modifier-meta">
                                      {option.max_per_item ? <span className="a11y-modifier-max text-sm">(Max. {option.max_per_item})</span> : null}
                                      {(option.price && option.price > 0) ?
                                        <span className="text-[var(--color-common-price)] fs-25 lh-25 text-right">
                                          +{new Currency(currency, locale).format(option.price)}
                                        </span> : ""
                                      }
                                    </span>
                                  </span>
                                  <div className="flex items-center gap-[37px]">
                                    <motion.button
                                      onClick={() => handleExtraQuantityChange(extraGroup.id, groupIndex, option.id, -1, option.max_per_item)}
                                      className="w-[59px] h-[59px] rounded-[15px] border-2 flex items-center justify-center hover:border-[var(--color-primary)] transition-colors"
                                      whileTap={{ scale: 0.9 }}
                                      whileHover={{ scale: 1.03 }}
                                      disabled={extraQuantity <= 0}
                                      style={{ borderColor: 'var(--color-text-muted)' }}
                                    >
                                      <Minus className="w-8 h-8 text-[#C0C0C0]" />
                                    </motion.button>

                                    <span className="text-bold fs-30 lh-25 min-w-[40px] text-center">
                                      {extraQuantity.toString().padStart(2, '0')}
                                    </span>

                                    <div className="relative w-[59px] h-[59px]">
                                      {((option.max_per_item ? extraQuantity >= option.max_per_item : false) || (extraGroup.max !== undefined ? selectedCount >= extraGroup.max : false)) && (
                                        <div 
                                          className="absolute inset-0 z-10 cursor-pointer rounded-[15px]"
                                          onClick={() => triggerShake(groupIndex)}
                                        />
                                        )}
                                      <motion.button
                                        onClick={() => handleExtraQuantityChange(extraGroup.id, groupIndex, option.id, 1, option.max_per_item)}
                                        className="w-[59px] h-[59px] rounded-[15px] border-2 flex items-center justify-center hover:border-[var(--color-primary)] transition-colors disabled:opacity-50"
                                        whileTap={{ scale: 0.9 }}
                                        whileHover={{ scale: 1.03 }}
                                        disabled={(option.max_per_item ? extraQuantity >= option.max_per_item : false) || (extraGroup.max !== undefined ? selectedCount >= extraGroup.max : false)}
                                        style={{ borderColor: 'var(--color-text-muted)' }}
                                      >
                                        <Plus className="w-8 h-8 text-[var(--color-common-price)]" />
                                      </motion.button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )
                          )
                        )}

                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Botones fijos inferiores */}
          <div className="absolute bottom-0 left-0 right-0 bg-back px-8 py-6">
            <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
              <DrawerClose asChild>
                <motion.button
                  className="cursor-pointer btn btn-blank gap-2 w-[200px] h-[120px] border-none secondary-font-bold"
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.1 }}
                  onClick={handleBack}
                >
                  <ChevronLeft className="w-8 h-8 text-bold" />
                  <span className="t fs-28">{editMode ? 'Cancelar' : 'Volver'}</span>
                </motion.button>
              </DrawerClose>
              <motion.button
                onClick={handleAddToCart}
                className={`cursor-pointer btn w-[650px] h-[120px] shadow-lg ${isValid ? 'btn-primary' : 'btn-muted'}`}
                style={{ opacity: isValid ? 1 : 0.5 }}
                whileTap={isValid ? { scale: 0.8 } : {}}
                whileHover={isValid ? { scale: 1.03 } : {}}
                transition={{
                  boxShadow: isValid ? { delay: 0, duration: 2, repeat: Infinity, ease: 'linear' } : {},
                  scale: { duration: 0.2, ease: 'easeOut' }
                }}
                animate={isValid ? { boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)'] } : { boxShadow: '0px 0px 0px 0px #000000' }}
              >
                <span className="fs-32 lh-38 main-font-bold">
                  {editMode ? 'Guardar cambios' : 'Agregar al carro'} ({new Currency(currency, locale).format(totalPrice)})
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}